import { Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { resolveLandingPath } from "@/app/auth/resolveNavAccess";
import { useNavContext } from "@/app/auth/useNavContext";
import { Button } from "@/shared/components/form/Button";
import useTranslations from "@/shared/lang/useTranslations";

/**
 * The denial screen for the whole dashboard: every guarded route and every module that latches a backend
 * 401/403 renders this. Its copy used to be hardcoded Spanish, which meant English and Portuguese users read
 * Spanish on what is now the app's most-reached fallback.
 */
export const NoAccessView = () => {
  const navigate = useNavigate();
  const navContext = useNavContext();
  const { t } = useTranslations("global");

  // The escape hatch used to be hardcoded to `/app/news`, which is INTERNAL-only — an organization
  // member who landed here would bounce straight back. Resolve it from the registry instead, and drop
  // the button entirely when the user has nowhere to go, rather than offering a dead end.
  const landingPath = navContext ? resolveLandingPath(navContext) : null;

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 py-12 text-center">
      <div className="rounded-full bg-amber-100 dark:bg-amber-900/30 p-4 mb-6">
        <Lock className="h-12 w-12 text-amber-600 dark:text-amber-400" />
      </div>
      <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-2">
        {t("no_access_title")}
      </h2>
      <p className="text-gray-600 dark:text-gray-400 max-w-md mb-8">{t("no_access_description")}</p>
      {landingPath && (
        <Button
          type="button"
          variant="primary"
          onClick={() => navigate(landingPath)}
          className="shadow-sm active:scale-[0.98]"
        >
          {t("no_access_back_home")}
        </Button>
      )}
    </div>
  );
};
